// #351, brief §5.4 and §15 rows 29 and 33: a program MODEL as data. Days with
// a role (in the name, dayRoleOf reads it), one block of typed rounds, and
// rows with a default exercise, its alternatives and a rule per level. One
// generator turns it into a program (programModelDraft.ts). The models are
// rows in DDB (templateRow.ts), never code. Keep free of React and any UI import.
import { z } from "zod";

import { LocalizedText } from "./localizedText.js";
import { ProgressionRuleSchema } from "./schemas/progressionRule.js";
import { RoundTypes } from "./schemas/programV3.js";
import { slugString } from "./schemas/slug.js";

export const MODEL_LEVELS = ["beginner", "intermediate", "advanced"] as const;
export const ModelLevel = z.enum(MODEL_LEVELS);
export type ModelLevel = z.infer<typeof ModelLevel>;

/** 45 min drops the optional rows and a set of each accessory; 60 drops the
 *  optional rows; 75 keeps everything (§15 row 33). */
export const SESSION_MINUTES = [45, 60, 75] as const;
export type SessionMinutes = (typeof SESSION_MINUTES)[number];

export const ModelRowWeight = z.enum(["main", "accessory", "optional"]);
export type ModelRowWeight = z.infer<typeof ModelRowWeight>;

/** The axes a model may use: what every athlete can derive. Never kg (§15
 *  row 29), and effort only in RPE: the generator writes the athlete's scale. */
export const MODEL_AXES = ["rpe", "percent-of-max", "history", "none"] as const;

const MAX_ROUNDS = 12;

const ModelRule = ProgressionRuleSchema.refine(
  (r) => (MODEL_AXES as readonly string[]).includes(r.axis),
  { message: "model_axis", path: ["axis"] },
).refine(
  // An effort deload is a delta (and setsDelta): a factor on RPE would not
  // mean the same once written in RIR. The factor stays for % of the MAX.
  (r) => r.axis !== "rpe" || r.deload?.factor === undefined,
  { message: "effort_deload_factor", path: ["deload", "factor"] },
);

const ModelChoice = z.object({
  /** Catalog slugs: the first is the default, the rest the alternatives. */
  exercises: z.array(slugString(64)).min(1).max(10),
  rule: ModelRule,
});

const ModelRow = z.object({
  id: slugString(64),
  weight: ModelRowWeight,
  /** A level with no entry skips the row. */
  byLevel: z.partialRecord(ModelLevel, ModelChoice),
});
export type ModelRow = z.infer<typeof ModelRow>;

const ModelDay = z.object({
  id: slugString(64),
  name: LocalizedText,
  rows: z.array(ModelRow).min(1).max(20),
});
export type ModelDay = z.infer<typeof ModelDay>;

/** The model's fields, with no cross-field check: templateRow extends it. */
export const ProgramModelShape = z.object({
  id: z.string().min(1).max(64),
  name: LocalizedText,
  description: LocalizedText,
  levels: z.array(ModelLevel).min(1).max(MODEL_LEVELS.length),
  /** One block of rounds, e.g. normal, normal, test, deload. */
  roundPattern: RoundTypes,
  /** How many blocks the athlete may pick. */
  blocks: z.array(z.number().int().min(1).max(MAX_ROUNDS)).min(1).max(MAX_ROUNDS),
  sessionsPerWeek: z.array(z.number().int().min(1).max(7)).min(1).max(7),
  days: z.array(ModelDay).min(1).max(7),
});
export type ProgramModel = z.infer<typeof ProgramModelShape>;

const dupes = (ids: readonly string[]): string[] => ids.filter((id, i) => ids.indexOf(id) !== i);

/** The checks across fields, for every schema built on ProgramModelShape. */
export const checkProgramModel = (m: ProgramModel, ctx: z.RefinementCtx): void => {
  const issue = (message: string, path: Array<string | number>) =>
    ctx.addIssue({ code: "custom", message, path });
  if (dupes(m.levels).length > 0) issue("duplicate_level", ["levels"]);
  if (m.blocks.some((b) => b * m.roundPattern.length > MAX_ROUNDS)) issue("too_many_rounds", ["blocks"]);
  if (dupes(m.days.map((d) => d.id)).length > 0) issue("duplicate_day_id", ["days"]);
  const rowIds = m.days.flatMap((d) => d.rows.map((r) => r.id));
  if (dupes(rowIds).length > 0) issue("duplicate_row_id", ["days"]);
  m.days.forEach((day, d) => {
    day.rows.forEach((row, r) => {
      const keys = Object.keys(row.byLevel) as ModelLevel[];
      if (keys.length === 0) issue("row_without_level", ["days", d, "rows", r, "byLevel"]);
      if (keys.some((k) => !m.levels.includes(k))) issue("level_not_offered", ["days", d, "rows", r, "byLevel"]);
    });
    // Even at 45 minutes, each level keeps something on every day.
    for (const level of m.levels) {
      if (!day.rows.some((row) => row.weight !== "optional" && row.byLevel[level]))
        issue("day_empty_for_level", ["days", d, "rows"]);
    }
  });
};

export const ProgramModel = ProgramModelShape.superRefine(checkProgramModel);
